import { AxiosResponse } from "axios";
import { SERVERURL } from "../globals/constants";
import { Profile } from "../models/Profile";
import { Set } from "../models/global/Set";
import { Invoice, InvoiceDto } from "../models/Invoice";
import { InvoiceStore } from "../stores/useInvoice";
import axios from "./axiosConfig";
import { InvoiceCreateDto } from "../models/dto/CreateInvoiceDto";

export const getAllInvoices = async (set: Set<InvoiceStore>, user: Profile) => {
  const response = await axios.get<Invoice[]>(`${SERVERURL}/invoice/getall`, {
    headers: {
      Authorization: `Bearer ${user.token}`,
      "Content-Type": "application/json;charset=UTF-8",
    },
  });

  set((state) => ({ ...state, invoices: response.data }));

  return response.data;
};

export const getInvoice = async (user: Profile, invoiceId: number) => {
  const { data } = await axios.get<Invoice>(
    `${SERVERURL}/invoice/get/${invoiceId}`,
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const importInvoice = async (
  user: Profile,
  invoice: InvoiceDto
): Promise<Invoice> => {
  try {
    const response: AxiosResponse = await axios.post<Invoice>(
      `${SERVERURL}/invoice/import`,
      invoice,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const createInvoice = async (
  user: Profile,
  invoice: InvoiceCreateDto
): Promise<Invoice> => {
  try {
    const response: AxiosResponse = await axios.post<Invoice>(
      `${SERVERURL}/invoice/create`,
      invoice,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const updateInvoice = async (
  user: Profile,
  invoice: InvoiceDto,
  invoiceId: number
): Promise<number> => {
  try {
    const response: AxiosResponse = await axios.put(
      `${SERVERURL}/invoice/update/${invoiceId}`,
      invoice,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const deleteInvoice = async (user: Profile, invoiceId: number) => {
  try {
    const response: AxiosResponse = await axios.delete(
      `${SERVERURL}/invoice/remove/${invoiceId}`,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const uploadFile = async (
  user: Profile,
  file: File,
  invoiceId: number
) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response: AxiosResponse = await axios.post(
      `${SERVERURL}/invoice/uploadFile/${invoiceId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const downloadInvoicesZip = async (
  user: Profile,
  ids: number[]
): Promise<void> => {
  const response = await axios.post(
    `${SERVERURL}/invoice/download`,
    { ids },
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
      responseType: "blob",
    }
  );

  const contentDisposition = response.headers["content-disposition"];
  let fileName = "invoices.zip";

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+?)"?$/);
    if (match?.[1]) {
      fileName = match[1];
    }
  }

  const blob = new Blob([response.data], {
    type: "application/zip",
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
